import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Reveal, Overline } from "../components/Reveal";
import { ProjectCard } from "../components/ProjectCard";
import api from "../lib/api";

const CATEGORIES = [
    { id: "all", label: "All" },
    { id: "residential", label: "Residential" },
    { id: "commercial", label: "Commercial" },
];

const MARKET_TABS = [
    { id: "india", label: "India" },
    { id: "dubai", label: "Dubai" },
];

const norm = (s) => (s || "").trim().toLowerCase();

const Opportunities = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const category = norm(searchParams.get("category") || "all");
    const market = norm(searchParams.get("market") || "india");

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "all") next.delete(key);
        else next.set(key, value);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        api.get("/projects")
            .then((r) => setProjects(r.data))
            .catch(() => setProjects([]))
            .finally(() => setLoading(false));
    }, []);

    const projectCountry = (p) => {
        const c = norm(p.country);
        if (c) return c;
        return ["noida", "greater noida", "gurugram"].includes(norm(p.market)) ? "india" : norm(p.market);
    };

    const visible = projects.filter((p) => {
        const okCategory = category === "all" || norm(p.category) === category;
        return okCategory && projectCountry(p) === market;
    });

    return (
        <main data-testid="opportunities-page">
            <section className="bg-charcoal pb-20 pt-40 text-ivory md:pb-28 md:pt-52">
                <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                    <Reveal>
                        <Overline>Opportunities</Overline>
                        <h1 className="mt-6 max-w-3xl font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl">
                            Selected <span className="italic text-gold">Opportunities.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-ivory/70">
                            A considered selection of residential and commercial opportunities across
                            India and Dubai — each verified against developer records and official
                            registrations before being presented.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-28">
                {!loading && projects.length > 0 && (
                    <Reveal data-testid="market-filters">
                        <div className="flex items-end gap-8 md:gap-12" data-testid="market-selector">
                            {MARKET_TABS.map((m) => (
                                <button
                                    key={m.id}
                                    data-testid={`market-tab-${m.id}`}
                                    onClick={() => setParam("market", m.id)}
                                    className={`border-b-2 pb-3 font-serif text-3xl font-light tracking-tight transition-colors duration-500 md:text-4xl ${
                                        market === m.id
                                            ? "border-gold text-ink"
                                            : "border-transparent text-ink/35 hover:text-ink"
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-9 flex flex-wrap items-center gap-3" data-testid="category-tabs">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    data-testid={`category-tab-${c.id}`}
                                    onClick={() => setParam("category", c.id)}
                                    className={`border px-7 py-3 text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-500 ${
                                        category === c.id
                                            ? "border-ink bg-ink text-ivory"
                                            : "border-ink/20 text-ink/60 hover:border-gold hover:text-ink"
                                    }`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>

                    </Reveal>
                )}

                {loading ? (
                    <div className="mt-20 grid gap-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-loading">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="animate-pulse">
                                <div className="aspect-[4/3] bg-ink/5" />
                                <div className="mt-6 h-6 w-2/3 bg-ink/5" />
                                <div className="mt-3 h-4 w-1/3 bg-ink/5" />
                            </div>
                        ))}
                    </div>
                ) : projects.length === 0 ? (
                    <Reveal className="mt-8 border border-ink/10 bg-white/60 px-8 py-16 text-center md:py-24" data-testid="opportunities-placeholder">
                        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">Curated, Not Listed</p>
                        <h2 className="mx-auto mt-4 max-w-xl font-serif text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl">
                            Looking for something specific?
                        </h2>
                        <p className="mx-auto mt-5 max-w-md text-[15px] font-light leading-relaxed text-ink/65">
                            Tell us your requirement and we'll start with the opportunities that make
                            sense for you.
                        </p>
                        <Link
                            to="/for-investors"
                            data-testid="opportunities-requirement-cta"
                            className="group mt-10 inline-flex items-center gap-3 bg-ink px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-ink"
                        >
                            Tell Us Your Requirement
                        </Link>
                    </Reveal>
                ) : visible.length === 0 ? (
                    <p className="mt-20 font-serif text-2xl font-light italic text-ink/50" data-testid="projects-empty">
                        No opportunities match this selection yet. Speak with us for current availability.
                    </p>
                ) : (
                    <div className="mt-16 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
                        {visible.map((p, i) => (
                            <ProjectCard key={p.id} project={p} index={i} />
                        ))}
                    </div>
                )}

                {!loading && projects.length > 0 && (
                    <Reveal className="mt-24 border-t border-ink/10 pt-8">
                        <p className="max-w-3xl text-[12px] font-light leading-relaxed text-ink/45">
                            Project details, availability and pricing are subject to change and should be
                            independently verified before making a property decision.
                        </p>
                    </Reveal>
                )}
            </section>
        </main>
    );
};

export default Opportunities;
